import { openDB, DBSchema, IDBPDatabase } from 'idb';

// ─── Interfaces del Dominio ────────────────────────────────────────────────

export interface ChatMessage {
  id: string; // UUID del mensaje
  cita_id: string;
  texto: string; // Este texto ya vendrá cifrado desde el ChatEngine si aplica
  emisor_id: string;
  timestamp: string;
}

export interface OutboxMessage extends ChatMessage {
  estado: 'pendiente' | 'enviando' | 'error';
  intentos: number;
}

// ─── Definición del Esquema ────────────────────────────────────────────────

interface AgendamientoDB extends DBSchema {
  messages: {
    key: string; // message id
    value: ChatMessage;
    indexes: {
      'by-cita': string; // índice por cita_id para cargar rápido
    };
  };
  outbox: {
    key: string;
    value: OutboxMessage;
    indexes: {
      'by-cita': string;
      'by-estado': string;
    };
  };
}

// ─── Implementación del Wrapper ────────────────────────────────────────────

const DB_NAME = 'agendamiento_local_db';
const DB_VERSION = 1;

let dbPromise: Promise<IDBPDatabase<AgendamientoDB>> | null = null;

function getDB() {
  if (!dbPromise) {
    dbPromise = openDB<AgendamientoDB>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        // Store de mensajes históricos
        if (!db.objectStoreNames.contains('messages')) {
          const store = db.createObjectStore('messages', { keyPath: 'id' });
          store.createIndex('by-cita', 'cita_id');
        }
        
        // Store de mensajes pendientes por enviar (Outbox)
        if (!db.objectStoreNames.contains('outbox')) {
          const store = db.createObjectStore('outbox', { keyPath: 'id' });
          store.createIndex('by-cita', 'cita_id');
          store.createIndex('by-estado', 'estado');
        }
      },
    });
  }
  return dbPromise;
}

/**
 * ChatLocalDB
 * 
 * Cumple con la directriz de Sovereign Storage (Almacenamiento Local Soberano).
 * Encapsula todas las transacciones de lectura/escritura en IndexedDB usando `idb`.
 */
export const ChatLocalDB = {
  
  // ─── MESSAGES (Historial) ───

  async saveMessage(msg: ChatMessage): Promise<void> {
    const db = await getDB();
    await db.put('messages', msg);
  },

  async saveMessagesBatch(msgs: ChatMessage[]): Promise<void> {
    const db = await getDB();
    const tx = db.transaction('messages', 'readwrite');
    await Promise.all([
      ...msgs.map(m => tx.store.put(m)),
      tx.done
    ]);
  },

  async getMessagesByCita(citaId: string): Promise<ChatMessage[]> {
    const db = await getDB();
    const msgs = await db.getAllFromIndex('messages', 'by-cita', citaId);
    // Ordenar por fecha
    return msgs.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
  },

  // ─── OUTBOX (Resiliencia Offline) ───

  async addToOutbox(msg: OutboxMessage): Promise<void> {
    const db = await getDB();
    await db.put('outbox', msg);
  },

  async getPendingOutbox(): Promise<OutboxMessage[]> {
    const db = await getDB();
    // Traemos los que están en estado 'pendiente' o 'error'
    const msgs = await db.getAll('outbox');
    return msgs.filter(m => m.estado !== 'enviando').sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
  },

  async removeFromOutbox(msgId: string): Promise<void> {
    const db = await getDB();
    await db.delete('outbox', msgId);
  },
  
  async markOutboxAsSending(msgId: string): Promise<void> {
    const db = await getDB();
    const msg = await db.get('outbox', msgId);
    if (msg) {
      msg.estado = 'enviando';
      await db.put('outbox', msg);
    }
  },

  async markOutboxAsError(msgId: string): Promise<void> {
    const db = await getDB();
    const msg = await db.get('outbox', msgId);
    if (msg) {
      msg.estado = 'error';
      msg.intentos += 1;
      await db.put('outbox', msg);
    }
  }
};
